import {PinIcon} from '@primer/octicons-react'
import type {ReactElement} from 'react'

import {LABELS} from '../constants/labels'
import {createIssueEventExternalUrl} from '../utils/urls'
import type {
  IssueCommentPinnedEventFragment,
  IssueCommentUnpinnedEventFragment as IssueCommentUnpinnedEventFragmentType,
} from './IssueCommentPinnedEvent'
import {IssueCommentCopyRendering, IssueCommentLink} from './IssueCommentPinShared'
import {RolledupIssueCommentPinEvent} from './RolledupIssueCommentPinEvent'
import {TimelineRow} from './row/TimelineRow'

export type IssueCommentUnpinnedEventFragment = IssueCommentUnpinnedEventFragmentType

type IssueCommentUnpinnedEventProps = {
  queryRef: IssueCommentUnpinnedEventFragment
  issueUrl: string
  onLinkClick?: (event: MouseEvent) => void
  highlightedEventId?: string
  refAttribute?: React.RefObject<HTMLLIElement | null>
  rollupGroup?: Record<string, Array<IssueCommentUnpinnedEventFragment | IssueCommentPinnedEventFragment>>
}

const getUnpinnedEventKey = (queryRef: IssueCommentUnpinnedEventFragment) =>
  [queryRef.databaseId ?? 'missing-db-id', queryRef.issueComment?.id ?? 'missing-comment-id', queryRef.createdAt].join(
    ':',
  )

export function IssueCommentUnpinnedEvent({
  queryRef,
  issueUrl,
  onLinkClick,
  highlightedEventId,
  refAttribute,
  rollupGroup,
}: IssueCommentUnpinnedEventProps): React.ReactElement {
  const {actor, createdAt, databaseId} = queryRef

  const highlighted = String(databaseId) === highlightedEventId

  return (
    <TimelineRow
      highlighted={highlighted}
      refAttribute={refAttribute}
      actor={actor}
      createdAt={createdAt}
      deepLinkUrl={createIssueEventExternalUrl(issueUrl, databaseId)}
      onLinkClick={onLinkClick}
      leadingIcon={PinIcon}
    >
      <TimelineRow.Main>
        <span data-testid="issue-comment-unpinned-copy">
          {rollupGroup ? (
            <RolledupIssueCommentPinEvent rollupGroup={rollupGroup} />
          ) : (
            <UnpinnedCommentRendering queryRefs={[queryRef]} rollup={false} />
          )}
        </span>{' '}
      </TimelineRow.Main>
    </TimelineRow>
  )
}

type UnpinnedCommentRenderingProps = {
  queryRefs: IssueCommentUnpinnedEventFragment[]
  rollup: boolean
}

export const UnpinnedCommentRendering = ({queryRefs, rollup}: UnpinnedCommentRenderingProps): ReactElement | null => {
  return (
    <IssueCommentCopyRendering
      queryRefs={queryRefs}
      rollup={rollup}
      label={LABELS.timeline.issueCommentUnpinned.leading}
      getItemKey={getUnpinnedEventKey}
      renderLink={queryRef => <UnpinnedCommentLink queryRef={queryRef} />}
      linkPrefix="a "
    />
  )
}

export const UnpinnedCommentLink = ({queryRef}: {queryRef: IssueCommentUnpinnedEventFragment}): ReactElement => {
  return (
    <IssueCommentLink
      linkText={LABELS.timeline.issueCommentUnpinned.comment}
      url={queryRef.issueComment?.url}
      testId="issue-comment-unpinned-link"
    />
  )
}
